import React from "react";
import { Play } from "lucide-react";

/**
 * FeedLandingTV — the big "Watch Now" TV display on `/` and `/feed`.
 * Pure presentational; parent owns `activeVideos` (post-region-filter),
 * `loading`, `parallaxY`, and the click handler.
 *
 * Props:
 *   loading       — bool, render the spinner instead of play button
 *   activeVideos  — array of videos visible after region filter
 *   activeRegion  — 'Global' | 'Americas' | … (drives copy)
 *   source        — 'all' | 'following' (drives "Watch Now"/"Watch Your Feed")
 *   parallaxY     — scroll-driven Y offset (capped) for subtle depth
 *   onPlayAll     — () => void, fires when user taps the TV
 */
export const FeedLandingTV = ({ loading, activeVideos, activeRegion, source, onPlayAll }) => (
  <div
    className="flex justify-center mb-4"
    data-testid="landing-tv"
  >
    <div className="relative">
      {/* TV Frame */}
      <div className="relative bg-gray-900 rounded-lg border-4 border-gray-700 shadow-2xl shadow-black/50 p-2">
        {/* TV Screen — Iter-83: removed "MADE BY HUMANS / NOT AI" inside
            the TV because that text was bleeding onto the GlobalEyes.TV
            brand label below on mobile. The two slogans now live below
            the TV stand as a quiet attribution line, never overlapping. */}
        <div
          className="relative bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded w-[14rem] h-[12.5rem] sm:w-[420px] sm:h-64 md:w-[560px] md:h-72 max-w-full flex items-center justify-center cursor-pointer group overflow-hidden"
          onClick={onPlayAll}
          data-testid="home-tv-play-btn"
        >
          {/* Screen Effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent" />
          <div
            className="absolute inset-0 opacity-5"
            style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)" }}
          />

          {loading ? (
            <div className="animate-spin h-12 w-12 border-4 border-emerald-500 border-t-transparent rounded-full" />
          ) : (
            // Single TV-screen layout for every state — empty feeds render the same
            // play button & labels; only the subtitle copy changes per `source`.
            <div className="relative z-10 flex flex-col items-center py-4">
              {/* Live Indicator */}
              <div className="flex items-center gap-2 bg-black/50 px-3 py-1 rounded-full border border-red-500/30 mb-2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-gray-300 text-xs font-medium">LIVE</span>
              </div>
              <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center shadow-lg transition-all ${
                activeVideos.length > 0
                  ? "bg-emerald-600/90 group-hover:bg-emerald-500 shadow-emerald-500/30 group-hover:scale-110"
                  : "bg-gray-700/70 shadow-black/40"
              }`}>
                <Play className="h-8 w-8 sm:h-10 sm:w-10 text-white ml-1" fill="white" />
              </div>
              <p className="text-white font-semibold mt-2 text-sm sm:text-base">
                {source === "following" ? "Watch Your Feed" : "Watch Now"}
              </p>
              <p className="text-emerald-400 text-xs sm:text-sm">
                {activeVideos.length} videos{activeRegion !== "Global" ? ` · ${activeRegion}` : ""}
              </p>
              <p className="text-gray-500 text-[10px] sm:text-xs">
                {activeVideos.length === 0 && source === "following"
                  ? "Follow creators to see their videos here"
                  : activeVideos.length === 0
                  ? (activeRegion === "Global" ? "Be the first to share your view!" : "Try a different region")
                  : source === "following"
                  ? "from creators you follow"
                  : (activeRegion === "Global" ? "from around the world" : `produced in ${activeRegion}`)}
              </p>
            </div>
          )}
        </div>

        {/* TV Brand */}
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-gray-700 px-4 py-0.5 rounded-b text-[10px] text-gray-400 font-medium tracking-widest">
          GlobalEyes.TV
        </div>
      </div>

      {/* TV Stand */}
      <div className="flex justify-center">
        <div className="w-24 h-3 bg-gray-700 rounded-b-lg" />
      </div>
      <div className="flex justify-center -mt-0.5">
        <div className="w-40 h-2 bg-gray-800 rounded-full" />
      </div>

      {/* Made-by-humans attribution — lives OUTSIDE the TV now (Iter-83)
          so it can never bleed onto the brand label. */}
      <p className="text-center text-emerald-400/90 text-[10px] sm:text-xs font-bold tracking-wider mt-3">
        MADE BY HUMANS · NOT AI
      </p>
    </div>
  </div>
);
